import { APIComponentAttributes, APITagAttributes } from '@ardoq/api-types';
import {
  ArdoqError,
  getArdoqErrorTraceId,
  isArdoqError,
} from '@ardoq/common-helpers';
import { confirm } from '@ardoq/modal';
import { I18nLocale, t } from '@ardoq/i18n';
import { CellValueChangedEvent, GridApi, IRowNode } from 'ag-grid-enterprise';
import { isEqual } from 'lodash';
import { tagOperations, PendingTagChange } from '@ardoq/core';
import {
  ColumnDefinitionArdoqMetadata,
  ExtendedRowNode,
  InventoryContext,
} from '../types';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import { uniq } from 'lodash';

const showSaveTagsFailedDialog = async (
  i18nLocale: I18nLocale,
  result: ArdoqError
): Promise<void> => {
  const traceId = getArdoqErrorTraceId(result);
  await confirm({
    title: t(
      i18nLocale,
      'inventory:saveTagsFailed.title',
      'Editing tags failed'
    ),
    text: t(
      i18nLocale,
      'inventory:saveTagsFailed.text',
      'Your changes could not be saved and will be reverted.'
    ),
    text2:
      traceId &&
      t(
        i18nLocale,
        'inventory:saveTagsFailed.traceId',
        { traceId },
        `Trace ID: ${traceId}`
      ),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  });
};

export const saveTags = async (
  event: CellValueChangedEvent<
    APIComponentAttributes,
    string[],
    InventoryContext
  >
) => {
  const columnContext = event.colDef.context as ColumnDefinitionArdoqMetadata;
  const component = event.data;
  if (
    !component?._id ||
    isEqual(event.oldValue ?? [], event.newValue ?? []) ||
    !(columnContext.inventoryColumnType === 'tag')
  ) {
    return;
  }
  const { commands, tagsByWorkspaceId, i18nLocale } = event.context;

  const { tagsToAdd, tagsToRemove } = tagOperations.diffTagNames(
    event.newValue ?? [],
    event.oldValue ?? []
  );

  const pendingChange: PendingTagChange = {
    kind: 'component',
    entityId: component._id,
    workspaceId: component.rootWorkspace,
    tagsToAdd,
    tagsToRemove,
  };

  const existingTagsInWorkspaceOfEditedComponent =
    tagsByWorkspaceId[component.rootWorkspace] ?? [];
  const tagIntents = tagOperations.buildTagIntents(
    [pendingChange],
    existingTagsInWorkspaceOfEditedComponent
  );
  if (tagIntents.length === 0) {
    return;
  }

  commands.trackTagUpdates([pendingChange]);
  const result = await commands.saveTags(tagIntents);

  if (isArdoqError(result)) {
    await showSaveTagsFailedDialog(i18nLocale, result);
  }
};

export const collectPendingTagChangesFromNodes = (
  api: GridApi<APIComponentAttributes>
): tagOperations.PendingTagChange[] => {
  const pendingTagChanges: tagOperations.PendingTagChange[] = [];
  const collect = (node?: IRowNode<APIComponentAttributes> | null) => {
    const pendingTagChange = (node as ExtendedRowNode | undefined)
      ?._pendingTagChange;
    if (pendingTagChange) {
      pendingTagChanges.push(pendingTagChange);
    }
  };

  api.forEachNode(collect);

  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (
      node?.data &&
      !createComponentsOperations.isComponentNotPersisted(node.data._id)
    ) {
      collect(node);
    }
  }

  return pendingTagChanges;
};

export const clearPendingTagChangesFromNodes = (
  api: GridApi<APIComponentAttributes>
) => {
  api.forEachNode(node => {
    (node as ExtendedRowNode)._pendingTagChange = undefined;
  });
  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (node) {
      (node as ExtendedRowNode)._pendingTagChange = undefined;
    }
  }
};

export const multiUpdateTags = async (
  pendingTagChanges: PendingTagChange[],
  context: InventoryContext,
  api: GridApi<APIComponentAttributes>
): Promise<APITagAttributes[] | ArdoqError> => {
  if (pendingTagChanges.length === 0) {
    return Promise.resolve([]);
  }

  const workspaceIds = uniq(pendingTagChanges.map(it => it.workspaceId));
  const existingTagsInWorkspaceOfEditedComponents = workspaceIds.flatMap(
    workspaceId => context.tagsByWorkspaceId[workspaceId] ?? []
  );

  const tagIntents = tagOperations.buildTagIntents(
    pendingTagChanges,
    existingTagsInWorkspaceOfEditedComponents
  );
  if (tagIntents.length === 0) {
    clearPendingTagChangesFromNodes(api);
    return Promise.resolve([]);
  }

  context.commands.trackTagUpdates(pendingTagChanges);
  const result = await context.commands.saveTags(tagIntents);
  if (isArdoqError(result)) {
    await showSaveTagsFailedDialog(context.i18nLocale, result);
  }
  clearPendingTagChangesFromNodes(api);
  return result;
};
